import React, {useMemo} from 'react';
import {motion} from 'framer-motion';
import TickerText from './tickerText';
import '../../styles/main.scss';
import './styles/ticker.scss';
import * as Hyperlive from '../../hyperlive/utils.js';
import {ANIMATION_DURATION_S, NIKE_EASE} from '../../constants/nikerise';
import {useInView} from 'react-intersection-observer';
import {getChannelInfo} from "../../hyperlive/utils.js";

const getWidth = ({
                    content,
                    nextIndex,
                    firstIndex,
                    poleWidth,
                    totalWidth,
                  }) => {
  if (nextIndex === -1) return totalWidth;

  // Check out why the first pole is always at the 0px left position
  return !!content[nextIndex].pole
    ? (content[nextIndex].pole - content[firstIndex].pole) * poleWidth
    : content[nextIndex].start_location - content[firstIndex].start_location;
};

const getZoneData = (content, poleWidth, totalWidth) => {
  const firstIndex = content.findIndex(zone => zone.type === 'divider');
  const nextIndex = content.findIndex(
    (zone, index) => index > firstIndex && zone.type === 'divider',
  );

  const endIndex = nextIndex === -1 ? content.length : nextIndex;
  const innerContent = content
    .slice(firstIndex + 1, endIndex)
    .reduce((acc, curr) => {
      if (curr.repeats) {
        return [...acc, ...[...Array(curr.repeats)].map(() => curr)];
      }
      return [...acc, curr];
    }, []);

  const width = getWidth({
    content,
    nextIndex,
    firstIndex,
    poleWidth,
    totalWidth,
  });

  const rest = content.slice(endIndex);
  return [
    {...content[firstIndex], children: innerContent, width},
    ...(rest.length ? getZoneData(rest, poleWidth, totalWidth - width) : rest),
  ];
};

const getUsedLanguages = zones =>
  Hyperlive.getStoreLanguages().filter(language =>
    zones.some(zone =>
      zone.some(({type, content}) =>
        type === 'text' ? Object.keys(content).includes(language) : false,
      ),
    ),
  );

const dynamicComponents = {
  text: ({text, content, language, elementSize, height, isActiveRow, rotate = "0"}) => {
    const {ref, inView} = useInView({
      threshold: 0,
    });
    return (
      <div
        className="text"
        style={{maxHeight: `${height}px`}}
        // style={{ maxHeight: `${elementSize}px` }} BASELINE OFF?
        ref={ref}
      >
        <TickerText
          language={language}
          text={content[language]}
          elementSize={elementSize}
          height={height}
          start={inView && isActiveRow}
          rotate={rotate}
        />
      </div>
    );
  },
  divider: ({elementSize, content, dividerHeightMultiplier = 1, dividerWidth = 4}) => {
    const isIcon = ["swoosh", "jumpman", "divider"].includes(content);

    let width = isIcon ? 90 : 0;
    if (content === 'divider') {
      width = 10;
    }

    return (
      <div className="icon" style={{
        opacity: isIcon ? 1 : 0,
        width,
        height: `${elementSize * 0.7}px`
      }}>

        {
          content === 'swoosh' && (
            <svg
              style={{
                height: `${elementSize * 0.7}px`,
                position: 'absolute'
              }}
              width="90"
              height="32"
              viewBox="0 0 90 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24.671 28.196C17.641 31.1607 6.60893 34.5747 2.00723 27.1927C-0.555939 23.1049 1.0629 16.7262 4.85519 10.9014C7.25347 7.18799 10.3563 3.77401 13.444 0.375C11.7353 3.16009 6.87874 12.4737 13.2192 17.1305C15.6924 18.9123 20.0393 19.092 25.4355 17.6995L89.9493 0.659499L24.671 28.196Z"
                fill="white"
              />
            </svg>
          )
        }
        {
          content === 'jumpman' && (
            <svg
              style={{
                height: `${elementSize * .7}px`,
                transform: `scale(1.25)`,
                position: 'absolute'
              }}
              width="99"
              height="90"
              viewBox="0 0 33 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fill="white"
                    d="M18.2686 2.0019C18.2686 1.92875 18.2442 1.87998 18.2442 1.80682C18.2198 0.855791 18.927 0.0510742 19.878 0.00230354C20.8047 -0.0464672 21.6094 0.685093 21.6581 1.63612C21.7069 2.58715 20.9754 3.39187 20.0243 3.41625C19.9756 3.41625 19.9024 3.41625 19.8536 3.41625C19.8049 3.41625 19.7805 3.44064 19.7561 3.46502C19.6586 3.61133 19.9999 3.58695 19.7561 4.24535C19.7561 4.24535 19.7561 4.31851 19.7317 4.39167C19.7073 4.63552 19.5854 5.39147 19.561 5.80602C19.6098 5.85479 19.6342 5.90356 19.6098 6.02548C19.5854 6.22057 19.5366 6.68389 19.4391 6.97651C19.3659 7.22037 19.2684 7.26914 19.1952 7.34229C19.0733 7.87877 19.0245 8.31771 18.7075 8.95173C18.6588 9.43943 18.6344 9.56136 18.5124 9.80521C18.4881 10.5368 18.5368 10.6343 18.0247 11.756C17.8784 12.2925 18.0003 12.6583 18.0979 13.2192C18.2198 13.78 18.5856 14.0483 18.6344 14.6335C18.7319 15.9991 18.6831 16.9501 18.3417 18.0231L18.5856 18.6327C18.9026 18.779 19.6829 19.218 19.2196 19.8276C19.8292 20.169 20.7315 20.5348 21.4875 21.2176C21.7801 21.4858 22.0971 21.7784 22.3897 22.1198C22.9506 22.2174 22.8774 22.2661 23.292 22.5344C24.4868 23.3147 26.3889 24.851 27.4619 25.9239C27.6569 26.0459 27.7301 26.119 27.8764 26.2166C27.9008 26.2653 27.9008 26.2653 27.9008 26.2897C27.852 26.436 27.852 26.436 27.8033 26.5336C27.852 26.6067 27.974 26.6799 28.0471 26.7286C28.1203 26.753 28.1203 26.7286 28.2178 26.7043C28.3641 26.8506 28.5348 26.9481 28.5836 26.9481C28.7787 26.8993 28.7543 26.9481 28.9006 26.753C28.9738 26.7043 29.0469 26.753 29.0469 26.753C29.1201 26.6799 29.242 26.5336 29.4127 26.436C29.559 26.3629 29.8272 26.3629 29.8272 26.3629C29.9248 26.3629 29.9004 26.4604 29.876 26.4848C29.7785 26.5336 29.559 26.5823 29.4371 26.6799C29.1688 26.9481 28.9494 27.3139 28.9494 27.3139C29.4858 27.2651 29.8029 27.387 30.2174 27.3627C30.4613 27.3627 30.632 27.387 30.8758 27.192C30.8758 27.192 31.1197 27.0213 31.3391 26.9481C31.5586 26.8506 31.7537 26.9237 31.9244 27.0457C32.0463 27.192 32.1195 27.192 31.9244 27.4114C31.8512 27.4846 31.7537 27.5821 31.6074 27.7041C31.3635 27.9235 31.0465 28.2162 30.7295 28.4844C30.3881 28.7282 30.0223 28.9477 29.7785 29.0452C29.1932 29.4598 29.3395 29.3379 28.8518 29.7524C28.8031 29.8012 28.6324 29.8987 28.5592 29.9475C28.3641 30.0694 28.3153 29.9719 28.2178 29.728C28.2178 29.728 28.169 29.6549 28.0715 29.4598C27.974 29.2647 27.9252 29.0452 27.9496 29.0452C27.852 29.0209 27.3643 28.4356 27.3643 28.3137C27.2668 28.2893 26.8278 27.8504 26.8278 27.7528L26.4377 27.4358C25.9987 27.509 25.9012 27.2407 24.7307 26.3629C24.5844 26.3629 24.3893 26.2653 24.0967 26.0459C23.5846 25.6069 22.3409 24.6071 22.1215 24.4852C21.902 24.3633 21.6094 24.2413 21.4631 24.095C21.2192 24.0463 21.0729 24.0219 20.951 23.9731C20.829 23.9243 20.7071 23.8756 20.3657 23.8268C19.8292 23.7536 19.2928 23.5098 18.7563 23.2172C18.5124 23.0952 18.293 23.0221 18.0735 22.9245C17.5858 22.7051 17.1956 22.51 16.8542 22.4124C16.7323 22.3881 16.1471 22.193 15.8544 22.0223C15.7569 21.9735 15.6837 21.9003 15.6106 21.9003C15.4399 21.8516 15.3423 21.9247 15.2692 21.9491C14.879 22.1442 14.4889 22.3149 14.1475 22.4856C13.8305 22.6563 13.5378 22.827 13.2208 22.9977C12.9282 23.144 12.6112 23.2903 12.2698 23.4366C12.2698 23.4366 11.3188 23.8999 10.8067 24.0706C10.3433 24.4364 9.41671 25.0461 8.85584 25.3387C8.5876 25.4606 8.02674 25.802 7.78289 25.8508C7.63658 25.9727 7.12448 26.3141 6.73432 26.6067C6.41731 26.8262 6.19784 27.0213 6.19784 27.0213C6.02714 27.1676 6.02714 27.2651 5.68575 27.0944C5.58821 27.1676 5.51505 27.2164 5.46628 27.2651C5.22243 27.4358 5.19804 27.387 5.1005 27.387C4.9298 27.5334 4.90542 27.4602 4.7591 27.6309C4.66156 27.7772 4.7591 27.7528 4.56402 27.8016C4.51525 27.826 4.49087 27.8748 4.46648 27.8991C4.29578 28.0211 4.29578 28.4112 3.90562 28.4356C3.66176 28.5819 3.73492 28.8014 3.56422 28.777C3.56422 28.9477 3.24721 29.1916 3.17406 29.1916C2.66196 29.3623 2.63758 28.9721 2.02795 29.2403C1.95479 29.2647 1.83286 29.3623 1.68655 29.3866C1.41831 29.4354 1.1013 29.3623 0.90622 29.216C0.613596 28.9721 0.345357 28.5088 0.345357 28.5088C0.247815 28.2649 0.320971 28.1186 0.613596 27.9235C0.735522 27.8504 0.759908 27.7772 0.979376 27.7772C1.07692 27.7284 1.05253 27.8016 1.24761 27.7284C1.34516 27.6797 1.34516 27.7284 1.58901 27.7041C1.63778 27.6797 1.71094 27.6553 1.80848 27.6309C1.97918 27.5821 2.14987 27.509 2.14987 27.509C2.14987 27.509 2.19864 27.4602 2.36934 27.4846C2.51565 27.4358 2.66196 27.3383 2.73512 27.3139C2.73512 27.0944 2.75951 27.0944 2.85705 26.9969C2.97897 26.875 3.00336 26.8993 3.1009 26.9725C3.14967 26.9481 3.14967 26.9481 3.14967 26.8993C3.14967 26.7774 3.02774 26.7286 3.07652 26.4604C3.02774 26.3629 2.97897 26.2166 2.97897 26.1434C3.00336 26.0702 3.02774 26.0215 3.07652 25.9971C3.12529 25.9727 3.14967 25.9971 3.17406 26.0459C3.22283 26.119 3.2716 26.2897 3.2716 26.2897C3.29598 26.4848 3.34475 26.753 3.53984 26.6067C3.66176 26.4848 3.68615 26.1678 3.93 26.2409L4.1007 26.3872C4.24701 26.2653 4.24701 26.2653 4.34455 26.1922C4.34455 26.1922 4.22263 26.0459 4.34455 25.9483C4.41771 25.8752 4.51525 25.8264 4.71033 25.6313C5.19804 25.1192 5.44189 24.8754 5.97837 24.4608C7.02694 23.6317 8.22182 23.0708 8.95338 22.7782C9.19724 22.4856 9.44109 22.2661 9.97757 22.2417C10.636 21.1688 11.904 20.2178 12.1722 20.0714C12.4161 19.6569 12.5136 19.7057 12.8063 19.6325C13.0501 19.4374 13.0989 19.4374 13.2208 19.2423C13.3427 18.8278 13.0257 17.7061 13.9036 17.6817C14.0987 17.4134 14.0499 17.4866 14.2694 17.2427C14.1475 16.9013 14.0987 16.6087 14.0499 16.3405C14.0499 16.3161 13.7085 16.0722 13.8061 15.7552C13.6598 15.5845 13.4159 15.17 13.3184 14.9749C13.2696 14.9749 13.2452 14.9505 13.1964 14.9505C13.1477 14.9505 13.0989 14.9505 13.0501 14.9261C12.9038 15.2675 12.7575 15.2919 12.5136 15.3651C12.0503 16.2673 11.904 16.6575 10.6604 17.3891C10.1727 17.9255 10.002 18.5352 10.002 18.5108C9.90441 18.6815 9.9288 18.9497 9.97757 19.0716C9.90441 19.2667 9.9288 19.2911 9.9288 19.2911C9.95318 19.3643 10.0263 19.4618 10.0995 19.4862C10.2458 19.535 10.3921 19.535 10.3677 19.6813C10.3433 19.8764 10.0751 19.852 9.95318 19.8276C9.46548 19.73 9.63617 19.413 9.31916 19.5106C9.07531 19.6569 8.97777 20.1202 8.5876 20.0471C8.53883 20.0227 8.56322 19.9007 8.5876 19.8276C8.63637 19.7057 8.73392 19.6325 8.68514 19.5837C8.44129 19.6569 7.9292 19.8032 7.9292 19.8032C7.68535 19.852 7.41711 19.73 7.66096 19.5837C7.7585 19.535 7.9292 19.4862 8.12428 19.3643C8.12428 19.3643 8.22182 19.2423 8.02674 19.2911C7.80727 19.3643 7.51465 19.3887 7.29518 19.3643C7.29518 19.3643 6.83186 19.2911 6.78309 19.2423C6.73432 19.218 6.68555 19.0716 6.80747 19.0716C6.97817 19.0716 7.44149 19.0473 7.78289 18.9497C7.95358 18.8766 8.27059 18.7059 8.46568 18.6083C8.46568 18.6083 8.66076 18.3157 8.78269 18.2669C8.97777 18.0718 9.12408 17.9499 9.29478 17.7304C9.46548 17.4134 9.63617 16.9013 10.1727 16.121C10.4165 15.7552 10.7335 15.3407 11.0993 14.9749C11.0993 14.9749 11.2212 14.3653 11.7089 13.9507C11.8309 13.7069 12.0016 13.4142 12.1479 13.1216C12.221 13.0241 12.2698 12.9265 12.3429 12.829C12.4893 12.5851 12.66 12.1462 13.2696 12.0974C13.2696 12.0974 13.5134 11.9267 13.6354 11.7804C13.7817 11.6585 13.7573 11.439 13.8792 11.3171C13.7817 11.3415 13.3184 10.9757 13.294 10.488C13.2696 9.97591 13.4403 9.56136 13.7573 9.26873C14.0987 8.92734 14.4645 8.78103 14.9034 8.80541C15.4399 8.90295 15.5374 9.07365 15.7081 9.26873C15.8788 9.43943 15.9276 9.34189 16.0007 9.53697C16.4397 9.6589 16.4153 9.61013 16.4153 9.95152C16.4885 10.0491 16.586 10.1466 16.586 10.3417C16.7567 10.0247 16.7811 9.97591 17.1956 9.63451C17.2932 9.34189 17.3663 9.07365 17.4639 8.78103C17.5614 8.51279 17.659 8.24455 17.7321 8.04947C17.659 7.46422 17.8296 7.36668 18.0735 6.87897C18.0247 6.80582 18.0491 6.78143 18.0735 6.68389C18.171 6.34249 18.3174 5.90356 18.4149 5.63532C18.4149 5.63532 18.4393 5.51339 18.5612 5.51339C18.6831 5.14761 18.8294 4.46482 18.8782 4.3429C19.0001 3.95273 18.927 3.8308 18.8538 3.61133C18.8294 3.53818 18.8538 3.44064 18.8051 3.36748C18.6831 3.14801 18.5612 2.85539 18.4637 2.63592C18.3661 2.48961 18.293 2.0019 18.2686 2.0019C18.2198 1.73366 18.2686 2.0019 18.2686 2.0019Z"></path>
            </svg>
          )
        }
        {
          content === 'divider' && (
            <svg
              style={{
                height: `${elementSize * .7}px`,
                transform: `scaleY(${dividerHeightMultiplier + .5})`,
                position: 'absolute',
              }}
              width="10"
              height="90"
              viewBox="0 0 10 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/*<line*/}
              {/*  x1="45"*/}
              {/*  y1="0"*/}
              {/*  x2="45"*/}
              {/*  y2="90"*/}
              {/*  stroke="white"*/}
              {/*  strokeWidth={dividerWidth}*/}
              {/*/>*/}
              
              <rect
                fill={'white'}
                x="5"
                y="0"
                width={dividerWidth}
                height="90"
              />
            </svg>
          )
        }
      </div>
    )
  },
  swoosh: ({elementSize}) => (
    <div className="icon" style={{height: `${elementSize}px`}}>
      <svg
        style={{height: `${elementSize}px`}}
        width="90"
        height="32"
        viewBox="0 0 90 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M24.671 28.196C17.641 31.1607 6.60893 34.5747 2.00723 27.1927C-0.555939 23.1049 1.0629 16.7262 4.85519 10.9014C7.25347 7.18799 10.3563 3.77401 13.444 0.375C11.7353 3.16009 6.87874 12.4737 13.2192 17.1305C15.6924 18.9123 20.0393 19.092 25.4355 17.6995L89.9493 0.659499L24.671 28.196Z"
          fill="white"
        />
      </svg>
    </div>
  ),
};

const DynamicComponent = ({type, ...props}) => {
  const Component = dynamicComponents[type];
  return <Component {...props} />;
};

const sequenceLength = 20000;
const switchTime = sequenceLength / 2;

const elementHeightPercentage = 0.7;

const Ticker = ({
                  zones,
                  height,
                  poleWidth,
                  screenWidth,
                  totalWidth,
                  top,

                  // represents the inner content
                  left,
                  inset,
                  zIndex = 1,

                  // represents the absolute left position of the container.
                  offset = 0,
                  frameCount,
                  rotate = "0"
                }) => {
  const [languageIndex, setLanguageIndex] = React.useState(0);
  const [activeRow, setActiveRow] = React.useState(0);
  const elementSize = elementHeightPercentage * height;
  const languages = getUsedLanguages(zones);

  React.useEffect(() => {

    const main = () => {
      const languagesAmount = languages.length;

      setTimeout(() => {
        setLanguageIndex(index => (index + 1) % languagesAmount);
        setActiveRow(0);
        main();
      }, sequenceLength);

      setTimeout(() => {
        setActiveRow(row => (row + 1) % 2);
      }, switchTime);
    };
    main();
  }, [setActiveRow, setLanguageIndex]);

  const language = languages[languageIndex];

  const textAlign = useMemo(() => {
    // get param `text-align` from the url query string
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('text-align');
  }, []);

  const {
    dividerHeightMultiplier,
    dividerWidth,
  } = useMemo(() => {
    return {
      dividerHeightMultiplier: getChannelInfo('json_config.divider_height_multiplier', 1),
      dividerWidth: getChannelInfo('json_config.divider_width', 4),
    }
  }, [zones]);

  const zoneData = React.useMemo(
    () => zones.map(zone => getZoneData(zone, poleWidth, totalWidth)),
    [poleWidth, totalWidth],
  );

  const repeatArr = React.useMemo(
    () => [...Array(Math.ceil(totalWidth / screenWidth))],
    [screenWidth, totalWidth],
  );

  return (
    <div
      className="ticker-pixelmap"
      style={{
        width: screenWidth,
        top,
        height: repeatArr.length * height,
        zIndex,
        left: offset
      }}
    >
      {repeatArr.map((_, repeatIndex) => (
        <div
          className="ticker-wrapper"
          style={{
            top: repeatIndex * height,
            width: totalWidth,
            left: -1 * repeatIndex * screenWidth,
          }}
        >
          <div className="inner-ticker-wrapper" style={{height}}>
            {zoneData.map((zone, zoneIndex) => (
              <motion.div
                key={`m_${zoneIndex}`}
                className={`ticker-container ${zoneIndex &&
                'invert'}`}
                initial={{
                  height,
                  top: zoneIndex * height,
                  left,
                  padding: `0 ${inset}px`,
                }}
                animate={{
                  top: (zoneIndex - activeRow) * height,
                }}
                transition={{
                  ease: NIKE_EASE,
                  duration: ANIMATION_DURATION_S,
                }}
              >
                {zone.map((part, i) => {
                  return (
                    <div
                      key={`m${zoneIndex}_${i}`}
                      className="FastlaneTicker"
                      style={{
                        width: part.width,
                      }}
                    >
                      <DynamicComponent
                        {...part}
                        height={height}
                        elementSize={elementSize}
                        language={language}
                        dividerHeightMultiplier={dividerHeightMultiplier}
                        dividerWidth={dividerWidth}
                      />
                      <div
                        className={`children-container ${textAlign === 'left' ? 'left-align' : ''}`}
                      >
                        {part.children?.map(
                          (child, j) => (
                            <DynamicComponent
                              {...child}
                              key={`m${zoneIndex}_${i}_${j}`}
                              language={language}
                              elementSize={
                                elementSize
                              }
                              rotate={rotate}
                              height={height}
                              isActiveRow={
                                activeRow ===
                                zoneIndex
                              }
                              dividerHeightMultiplier={dividerHeightMultiplier}
                              dividerWidth={dividerWidth}
                            />
                          ),
                        )}
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Ticker;
